import { useSignUp } from '@clerk/nextjs';
import { OAuthStrategy } from '@clerk/types';
import { Button, Stack } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { isGoogleSSODisabled } from 'utils/browser.utils';
import { addLocalePrefix } from 'utils/routes.utils';

import { Routes } from 'constants/routes.constants';
import { useAfterSignInPath } from 'hooks/useAfterSignInPath';
import { useLocale } from 'hooks/useLocale';
import { useUserAuthenticationView } from 'services/datalayer/account';
import { useGenericClick } from 'services/datalayer/browse';

import Icon from 'components/@icons/Icon';

interface Props {
  afterSignInUrl: string;
  originSourceCategory: string;
  onEmailFlow: () => void;
}

const StrategyToClickItem: Record<string, string> = {
  oauth_apple: 'Authenticate apple',
  oauth_google: 'Authenticate google',
};

const AuthMethod = ({
  afterSignInUrl,
  originSourceCategory,
  onEmailFlow,
}: Props) => {
  useUserAuthenticationView({ originSourceCategory });
  const [isLoadingOAuth, setIsLoadingOAuth] =
    useState<Record<string, boolean>>();

  const handleGenericClick = useGenericClick();
  const { locale } = useLocale();
  const { signUp } = useSignUp();
  const { setAfterSignInUrl } = useAfterSignInPath();

  const handleSocialSignIn = (strategy: OAuthStrategy) => {
    if (strategy === 'oauth_google' && isGoogleSSODisabled()) {
      onEmailFlow();
      return;
    }

    setIsLoadingOAuth((prev) => ({ ...prev, [strategy]: true }));

    handleGenericClick({ clickItem: StrategyToClickItem[strategy] });
    setAfterSignInUrl(afterSignInUrl);
    signUp?.authenticateWithRedirect({
      strategy,
      redirectUrl: addLocalePrefix(locale, Routes.SsoCallback),
      redirectUrlComplete: afterSignInUrl,
    });

    // Avoids rate limiting the API when users spam the button
    // https://clerk.com/docs/backend-requests/resources/rate-limits#frontend-api-requests
    setTimeout(
      () => setIsLoadingOAuth((prev) => ({ ...prev, [strategy]: false })),
      3000,
    );
  };

  const handleEmailLinkFlow = () => {
    onEmailFlow();
  };

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Button
        variant="contained"
        color="primary"
        startIcon={<Icon name="LogoApple" />}
        onClick={() => handleSocialSignIn('oauth_apple')}
        loading={isLoadingOAuth?.['oauth_apple']}
      >
        <FormattedMessage id="account.apple.sign.in.label" />
      </Button>
      <Button
        variant="contained"
        color="primary"
        startIcon={<Icon name="LogoGoogle" />}
        onClick={() => handleSocialSignIn('oauth_google')}
        loading={isLoadingOAuth?.['oauth_google']}
      >
        <FormattedMessage id="account.google.sign.in.label" />
      </Button>
      <Button
        variant="text"
        color="primary"
        sx={{ mt: 1 }}
        onClick={handleEmailLinkFlow}
      >
        <FormattedMessage id="account.email.sign.in.label" />
      </Button>
    </Stack>
  );
};

export default AuthMethod;
